import apiClient from '@/api/ApiClient';

const state = {
  renovasjonsbeholdere: [],
  renovasjonsbeholdereLaster: false,
  renovasjonsbeholdereFeiler: false,
  varslerFeiler: false,
  varslerLaster: false,
  modal: {
    erSynlig: false,
    avfallstype: '',
  },
};

export const getters = {
  renovasjonsbeholdere: (state) => state.renovasjonsbeholdere,
  modalAvfallstype: (state) => state.modal.avfallstype,
  erModalSynlig: (state) => state.modal.erSynlig,
  unikeRenovasjonsbeholdere: (state) =>
    state.renovasjonsbeholdere.filter((element, index, self) => {
      return self.findIndex((beholder) => beholder.beholdertype === element.beholdertype) === index;
    }),
};

export const actions = {
  async hentRenovasjonsbeholdere({ commit }, postadresse) {
    commit('SETT_RENOVASJONSBEHOLDERE_LASTER', true);
    commit('SETT_RENOVASJONSBEHOLDERE_FEILER', false);

    try {
      const res = await apiClient.getRenovasjonsbeholdere(postadresse);

      if (res) {
        res.forEach((beholder, i) => {
          const type = beholder.beskrivelse.toLowerCase();
          if (type.includes('restavfall')) {
            res[i].beholdertype = 'restavfall';
          } else if (type.includes('papir')) {
            res[i].beholdertype = 'papiravfall';
          } else if (type.includes('hageavfall')) {
            res[i].beholdertype = 'hageavfall';
          } else if (type.includes('matavfall')) {
            res[i].beholdertype = 'matavfall';
          } else if (type.includes('glass') || type.includes('metall')) {
            res[i].beholdertype = 'glassinnsamling';
          } else {
            res[i].beholdertype = 'ukjent';
          }
          res[i].varselAktivert = false;
          res[i].varsel = {};
        });
        commit('SETT_RENOVASJONSBEHOLDERE', res);
      }
    } catch {
      commit('SETT_RENOVASJONSBEHOLDERE_FEILER', true);
    }
    commit('SETT_RENOVASJONSBEHOLDERE_LASTER', false);
  },

  async hentRenovasjonsInnhold({ commit, dispatch, getters }, payload) {
    commit('SLETT_RENOVASJONS_INNHOLD');
    commit('SETT_VARSLER_LASTER', true);

    await dispatch('hentRenovasjonsbeholdere', payload.postadresse);

    const promises = [];

    getters.renovasjonsbeholdere.forEach((beholder) => {
      const prom = dispatch('hentVarsel', { eksternId: payload.eksternId, abonnementstypeId: beholder.beholdertype });
      promises.push(prom);
    });

    const res = await Promise.all(promises);

    if (res) {
      commit('SETT_VARSLER_LASTER', false);
    }
  },

  async hentVarsel({ commit, rootState }, varsel) {
    try {
      const res = await apiClient.getAvfallAbonnement(
        rootState.representasjoner.currentRepresentation.identifikator,
        varsel
      );
      if (res) {
        if (Array.isArray(res) && res.length > 0) {
          commit('SETT_VARSEL_PAA_BEHOLDERTYPE', {
            beholdertype: varsel.abonnementstypeId,
            varsel: res,
            varselAktivert: true,
          });
        } else {
          commit('SETT_VARSEL_PAA_BEHOLDERTYPE', {
            beholdertype: varsel.abonnementstypeId,
            varsel: {},
            varselAktivert: false,
          });
        }
      }
    } catch (error) {
      if (error.status !== 404) {
        commit('SETT_VARSLER_FEILER', true);
      }
    }
  },

  async startVarsel({ commit, dispatch }, payload) {
    commit('SETT_VARSLER_LASTER', true);

    try {
      const res = await apiClient.updateAbonnement(payload.varselObjekt);
      if (res) {
        dispatch(
          'addGlobalMessage',
          {
            messageType: 'info',
            message: payload.txtInfo,
          },
          { root: true }
        );
        dispatch('leggTilVarsel', payload);
        dispatch('abonnementer/leggTilAbonnement', payload.varselObjekt, { root: true });
      }
    } catch {
      dispatch(
        'addGlobalMessage',
        {
          messageType: 'error',
          message: payload.txtError,
        },
        { root: true }
      );
      commit('SETT_VARSLER_FEILER', true);
    }
    commit('SETT_VARSLER_LASTER', false);
  },

  async stoppVarsel({ commit, dispatch }, payload) {
    commit('SETT_VARSLER_LASTER', true);

    try {
      await apiClient.stopAbonnement(payload.varselObjekt);
      dispatch(
        'addGlobalMessage',
        {
          messageType: 'info',
          message: payload.txtInfo,
        },
        { root: true }
      );
      dispatch('lukkModal');
      dispatch('fjernVarsel', payload);
      dispatch('abonnementer/fjernAbonnement', payload.varselObjekt, { root: true });
    } catch {
      dispatch(
        'addGlobalMessage',
        {
          messageType: 'error',
          message: payload.txtError,
        },
        { root: true }
      );
      commit('SETT_VARSLER_FEILER', true);
    }
    commit('SETT_VARSLER_LASTER', false);
  },

  async leggTilVarsel({ commit }, payload) {
    commit('SETT_VARSEL_PAA_BEHOLDERTYPE', {
      beholdertype: payload.beholdertype || payload.abonnementstypeId,
      varsel: payload.varselObjekt,
      varselAktivert: true,
    });
  },
  async fjernVarsel({ commit }, payload) {
    commit('SETT_VARSEL_PAA_BEHOLDERTYPE', {
      beholdertype: payload.beholdertype || payload.abonnementstypeId,
      varsel: {},
      varselAktivert: false,
    });
  },

  visModal({ commit }, avfallstype) {
    commit('SETT_MODAL_AVFALLSTYPE', avfallstype);
    commit('SETT_MODAL_SYNLIGHET', true);
  },

  lukkModal({ commit }) {
    commit('SETT_MODAL_AVFALLSTYPE', '');
    commit('SETT_MODAL_SYNLIGHET', false);
  },
};

export const mutations = {
  SLETT_RENOVASJONS_INNHOLD(state) {
    state.renovasjonsbeholdere = [];
  },
  SETT_RENOVASJONSBEHOLDERE(state, detaljer) {
    state.renovasjonsbeholdere = detaljer;
  },
  SETT_RENOVASJONSBEHOLDERE_LASTER(state, isLoading) {
    state.renovasjonsbeholdereLaster = isLoading;
  },
  SETT_RENOVASJONSBEHOLDERE_FEILER(state, hasError) {
    state.renovasjonsbeholdereFeiler = hasError;
  },
  SETT_VARSEL_PAA_BEHOLDERTYPE(state, obj) {
    const beholder = state.renovasjonsbeholdere.find((x) => x.beholdertype.toLowerCase() === obj.beholdertype);
    beholder.varselAktivert = obj.varselAktivert;
    beholder.varsel = obj.varsel;
  },
  SETT_VARSLER_FEILER(state, feiler) {
    state.varslerFeiler = feiler;
  },
  SETT_VARSLER_LASTER(state, laster) {
    state.varslerLaster = laster;
  },
  SETT_MODAL_AVFALLSTYPE(state, avfallstype) {
    state.modal.avfallstype = avfallstype;
  },
  SETT_MODAL_SYNLIGHET(state, erSynlig) {
    state.modal.erSynlig = erSynlig;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
